import { HttpClient, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { SecondParty } from '@app/core/model/second-party.model'
import { User } from '@app/core/model/user.model'
import { LanguageService } from '@app/core/service/language.service'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { downloadFile, getFilenameFromUrl, untilDestroyed } from '@app/shared/utils/utils'
import { OutputTemplate } from '@app/features/scope-overview/model/output-template.model'
import { FolderVersion } from '@core/model/folder-version.model'
import { environment } from '@envs/environment'
import {
  SNACKBAR_LENGTH_LONG,
  SNACKBAR_LENGTH_SHORT,
  SnackbarEventType,
  SnackbarService,
} from '@shared/utils/snackbar.service'
import { Observable, catchError, map, tap, throwError } from 'rxjs'
import { DocuSignSigner } from '../components/export-docusign/export-docusign.component'
import { ApprovalFlowService } from './approval-flow.service'
import { ScopeOverviewService } from './scope-overview.service'

@Injectable({ providedIn: 'root' })
export class ExportDocumentService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl
  private readonly EXPORT_MESSAGE_FAIL = 'Unable to save'

  private readonly destroy$

  public hasCustomXlsxTemplate: boolean

  public secondParty: SecondParty

  private warningDialogRef: MatDialogRef<any, any>

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private approvalFlowService: ApprovalFlowService,
    private snackbarService: SnackbarService,
    private scopeOverviewService: ScopeOverviewService,
    private lang: LanguageService
  ) {
    this.destroy$ = untilDestroyed()
  }

  private closeExportDialog() {
    const exportCloseDialog: ModalConfig = {
      title: 'Export Scope',
      body: 'Your download will begin shortly.',
      confirmText: 'Close',
      confirmCallback: () => {
        dialog.close()
      },
      cancelCallback: undefined,
      inputs: [],
      isMemberModal: undefined,
      limitBodyWidth: true,
      customContainerClass: 'w-370',
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data: exportCloseDialog })
  }

  downloadFile(url: string, scopeName: string): Observable<Blob> {
    return this.http.post(url, this.scopeOverviewService.getScopeExportRequestBody(), { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        const a = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)

        a.href = objectUrl
        a.download = getFilenameFromUrl(url, scopeName)
        document.body.appendChild(a)
        a.click()

        URL.revokeObjectURL(objectUrl)
        document.body.removeChild(a)

        return blob
      }),
      catchError((error: any) => {
        console.error('Download error:', error)
        this.snackbarService.showSnackbar('Download error', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR)
        return throwError(() => new Error('Failed to download file'))
      })
    )
  }

  setXlsxTemplateVisibility(element: ScopeVersion | FolderVersion) {
    this.hasCustomXlsxTemplate = element.identity.xlsxTemplate !== null && element.identity.xlsxTemplate !== undefined
  }

  makeDocuSignAvailableOutputNames(currentUser: User, currentScope: ScopeVersion) {
    const docusignOutputFiles: [{ name: string; selected: boolean }] = [{ name: 'Output Document', selected: false }]
    if (currentUser.hasPrivilege('SCOPE__EXPORT_XLSX')) {
      if (currentScope.identity.xlsxTemplate !== null && currentScope.identity.xlsxTemplate !== undefined) {
        docusignOutputFiles.push({ name: 'Output Spreadsheet', selected: false })
      }
    }
    return docusignOutputFiles
  }

  getSecondParty(id: number): Observable<SecondParty> {
    return this.scopeOverviewService.getSecondPartyByIdForScope(id).pipe(
      tap((secondParty: SecondParty) => {
        this.secondParty = secondParty
      })
    )
  }

  hasSetting(setting: string, currentUser: User) {
    return currentUser.company.hasApplicationSetting(setting)
  }

  getStatus(currentScope: ScopeVersion) {
    return this.approvalFlowService.isStateApproved(currentScope) &&
      (this.approvalFlowService.isStateAgencyApproved(currentScope.status) ||
        this.approvalFlowService.isStateClientApproval(currentScope.status))
      ? '__CLIENT_ACCEPTED'
      : currentScope.status
  }

  hasDocusignPermission(currentScope: ScopeVersion, currentUser: User): boolean {
    const status = this.getStatus(currentScope)

    const grants = {
      DRAFT: this.hasSetting('DOCUSIGN_OUTPUT_ON_DRAFT', currentUser),
      SUBMITTED: this.hasSetting('DOCUSIGN_OUTPUT_ON_REVIEW', currentUser),
      REVIEWED: this.hasSetting('DOCUSIGN_OUTPUT_ON_AGREEMENT', currentUser),
      AGENCY_APPROVED: this.hasSetting('DOCUSIGN_OUTPUT_ON_CLIENT_APPROVAL', currentUser),
      CLOSED: this.hasSetting('DOCUSIGN_OUTPUT_ON_CLOSED', currentUser),
      __CLIENT_ACCEPTED: this.hasSetting('DOCUSIGN_OUTPUT_ON_CLIENT_ACCEPTED', currentUser),
    }

    return grants[status]
  }

  hasAdobeSignPermission(currentScope: ScopeVersion, currentUser: User): boolean {
    const status = this.getStatus(currentScope)

    const grants = {
      DRAFT: this.hasSetting('ADOBESIGN_OUTPUT_ON_DRAFT', currentUser),
      SUBMITTED: this.hasSetting('ADOBESIGN_OUTPUT_ON_REVIEW', currentUser),
      REVIEWED: this.hasSetting('ADOBESIGN_OUTPUT_ON_AGREEMENT', currentUser),
      AGENCY_APPROVED: this.hasSetting('ADOBESIGN_OUTPUT_ON_CLIENT_APPROVAL', currentUser),
      CLOSED: this.hasSetting('ADOBESIGN_OUTPUT_ON_CLOSED', currentUser),
      __CLIENT_ACCEPTED: this.hasSetting('ADOBESIGN_OUTPUT_ON_CLIENT_ACCEPTED', currentUser),
    }

    return grants[status]
  }

  showWarningModal() {
    this.warningDialogRef = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Export ${this.lang.get('scope')}`,
        body: 'Your document is exporting, please DO NOT CLICK ‘download’ again. Check your Notifications tab in a few minutes for the download',
        confirmText: 'OK',
        confirmCallback: () => this.warningDialogRef.close(),
        limitBodyWidth: true,
      },
    })
  }

  closeWarningModal() {
    this.warningDialogRef?.close()
  }

  exportToCSV(scopeId: number, scopeName: string) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/csv`
    this.downloadFile(url, scopeName)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportToDocusign(scopeId: number, contacts: DocuSignSigner[], outputOptions: string[]) {
    const url = `${this.BASE_API_URL}scope/${scopeId}/docusign/export`

    this.snackbarService.showSnackbar('Sending document to DocuSign...', SNACKBAR_LENGTH_SHORT, SnackbarEventType.INFO)

    return this.http
      .post(url, { contacts, fileNames: outputOptions })
      .pipe(
        map((apiResponse) => {
          this.snackbarService.showSnackbar(
            'Document was successfully sent to DocuSign.',
            SNACKBAR_LENGTH_SHORT,
            SnackbarEventType.SUCCESS
          )
          return apiResponse
        }),
        catchError((error) => {
          this.snackbarService.showSnackbar(
            'Failed to send document to DocuSign. Please check that DocuSign is correctly configured.',
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.ERROR
          )
          return throwError(() => new Error(error))
        })
      )
      .pipe(this.destroy$())
      .subscribe()
  }

  exportToAdobesign(scopeId: number, contacts: DocuSignSigner[], outputOptions: string[]) {
    const url = `${this.BASE_API_URL}scope/${scopeId}/adobesign/export`

    this.snackbarService.showSnackbar('Sending document to AdobeSign...', SNACKBAR_LENGTH_SHORT, SnackbarEventType.INFO)

    return this.http
      .post(url, { contacts, fileNames: outputOptions })
      .pipe(
        map((apiResponse) => {
          this.snackbarService.showSnackbar(
            'Document was successfully sent to AdobeSign.',
            SNACKBAR_LENGTH_SHORT,
            SnackbarEventType.SUCCESS
          )
          return apiResponse
        }),
        catchError((error) => {
          const baseMessage = 'Failed to send document to AdobeSign.'
          const reason =
            error?.error?.message === this.EXPORT_MESSAGE_FAIL
              ? ' Error when generating the file for signing.'
              : ' Please contact your administrator.'

          this.snackbarService.showPrioritisedSnackbar(
            baseMessage + reason,
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.ERROR
          )
          return throwError(() => new Error(error))
        })
      )
      .pipe(this.destroy$())
      .subscribe()
  }

  exportToWordDoc(scopeId: number, scopeName: string, downloadFileName?: string) {
    this.showWarningModal()

    let url = `${this.BASE_API_URL}scope/${scopeId}/export/docx`

    if (downloadFileName) {
      url += `?downloadFileName=${downloadFileName}`
    }

    this.downloadFile(url, scopeName)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportOutputTemplateToWordDoc(template: OutputTemplate) {
    const url =
      template.templateType === 'SCOPE'
        ? `${this.BASE_API_URL}scope/${template.id}/export/download`
        : `${this.BASE_API_URL}sow/${template.id}/export/preview`

    this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(this.destroy$())
      .subscribe({
        next: (res: HttpResponse<Blob>) => downloadFile(res),
        error: () => this.showTemplateDownloadError(),
      })
  }

  exportOutputTemplateToPdf(template: OutputTemplate) {
    const url = `${this.BASE_API_URL}scope/${template.id}/export/preview`

    this.http
      .get<string>(url)
      .pipe(this.destroy$())
      .subscribe({
        next: (base64Pdf: string) => this.downloadBase64Pdf(base64Pdf, `${template.name}.pdf`),
        error: () => this.showTemplateDownloadError(),
      })
  }

  private downloadBase64Pdf(base64Pdf: string, fileName: string) {
    const bytes = Uint8Array.from(atob(base64Pdf), (char) => char.charCodeAt(0))
    const a = document.createElement('a')
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))

    a.href = objectUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()

    URL.revokeObjectURL(objectUrl)
    document.body.removeChild(a)
  }

  private showTemplateDownloadError() {
    this.snackbarService.showPrioritisedSnackbar(
      'Download error, please contact your administrator.',
      SNACKBAR_LENGTH_LONG,
      SnackbarEventType.ERROR
    )
  }

  exportToPdf(scopeId: number, scopeName: string) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/pdf`
    this.downloadFile(url, scopeName)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeToXlsxTemplate(scopeId: number) {
    this.showWarningModal()
    const url = `${this.BASE_API_URL}scope/${scopeId}/export/xlsx-template`
    this.http
      .post(url, this.scopeOverviewService.getScopeExportRequestBody())
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeToExcel(scopeId: number, scopeName: string) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/xlsx`
    this.downloadFile(url, scopeName)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeToExcelSummary(scopeId: number) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/summary`
    this.http
      .post(url, this.scopeOverviewService.getScopeExportRequestBody())
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeBreakdownXlsxTemplate(scopeId: number) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/scope-breakdown`
    this.http
      .get(url)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeComponentToExcelSummary(scopeId: number) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/component-summary`
    this.http
      .post(url, this.scopeOverviewService.getScopeExportRequestBody())
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }

  exportScopeDepartmentsHoursXlsxTemplate(scopeId: number) {
    this.showWarningModal()

    const url = `${this.BASE_API_URL}scope/${scopeId}/export/scope-departments`
    this.http
      .get(url)
      .pipe(
        this.destroy$(),
        tap(() => (this.closeExportDialog(), this.closeWarningModal()))
      )
      .subscribe()
  }
}
